import React, { useCallback, useState, useEffect } from 'react';
import pt_BR from 'date-fns/locale/pt-BR';
import { useTheme } from 'styled-components';
import { useHistory } from 'react-router-dom';

import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { format } from 'date-fns';
import Actions from '../../components/Actions';

import BRLMoneyFormat from '../../utils/BRLMoneyFormat';

import api from '../../services/api';

import {
  Container,
  Wrapper,
  MenuTable,
  Filter,
  Table,
  StatusField,
  Pagination,
} from './styles';
import { useToast } from '../../hooks/toast';
import Button from '../../newComponents/Button';
import { useAuth } from '../../hooks/auth';

const DiscountCoupon: React.FC = () => {
  const theme = useTheme();
  const history = useHistory();
  const { addToast } = useToast();
  const { rotinas, findRotinas } = useAuth();

  const [filter, setFilter] = useState('all');
  const [coupons, setCoupons] = useState<any[]>([]);

  const hasPermission = useCallback(
    (): boolean => findRotinas('promocoes')?.listar || false,
    [findRotinas],
  );

  useEffect(() => {
    if (rotinas && !hasPermission()) {
      addToast({
        type: 'error',
        title: 'Não autorizado.',
        description: 'Você não tem permissão.',
      });
      history.push('/monitor_pedidos');
    }
  }, [addToast, hasPermission, history, rotinas]);

  const handleGoToPage = useCallback(
    (path: string, params?: Record<string, unknown>) => {
      history.push(path, params);
    },
    [history],
  );

  const handleStatusColors = useCallback(
    (status: boolean) => {
      if (status) {
        return theme.colors.green;
      }

      return theme.colors.red;
    },
    [theme],
  );

  const handleDeleteDiscountCoupon = useCallback(
    async (id: number) => {
      try {
        setCoupons((oldState) => oldState.filter((coupon) => coupon.id !== id));

        await api.delete(`/cupom-desconto/${id}`);
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Ocorreu um erro',
          description: (err as any).response.data.message || 'Tente novamente',
        });
      }
    },
    [addToast],
  );

  useEffect(() => {
    (async () => {
      const response = await api.get(`/cupom-desconto?filter=${filter}`);

      setCoupons(response.data);
    })();
  }, [filter]);

  return (
    <Container>
      <Wrapper>
        <header>
          <div>
            <h1>Cupom de desconto</h1>
            <h3>
              Os cupons podem ser utilizados direto pelo seu cliente ou como uma
              forma de seus parceiros divulgarem um desconto.
            </h3>
          </div>
          <Button
            onClick={() => handleGoToPage('/cupom-desconto/criar')}
            disabled={!hasPermission()}
          >
            <span>Adicionar cupom</span>
          </Button>
        </header>
        <MenuTable>
          <ul>
            <li>
              <Filter
                isActive={filter === 'all'}
                onClick={() => setFilter('all')}
              >
                Todos
              </Filter>
            </li>
            <li>
              <Filter
                isActive={filter === 'active'}
                onClick={() => setFilter('active')}
              >
                Ativos
              </Filter>
            </li>
            <li>
              <Filter
                isActive={filter === 'inactive'}
                onClick={() => setFilter('inactive')}
              >
                Inativos
              </Filter>
            </li>
          </ul>
        </MenuTable>
        <Table>
          <thead>
            <tr>
              <th>Descrição</th>
              <th>Desconto</th>
              <th>Válido até</th>
              <th>N° de usos</th>
              <th>Cupons restantes</th>
              <th>Status</th>
              {hasPermission() && <th>Ações</th>}
            </tr>
          </thead>
          <tbody>
            {coupons.map((coupon: any) => (
              <tr key={coupon.id}>
                <td>{coupon.descricao}</td>
                <td>
                  {coupon.tipo_desconto === 1
                    ? Number(coupon.valor_desconto).toLocaleString(
                        'pt-BR',
                        BRLMoneyFormat,
                      )
                    : `${coupon.valor_desconto}%`}
                </td>
                <td>
                  {coupon.data_termino
                    ? format(new Date(coupon.data_termino), "dd'/'MM'/'yyyy", {
                        locale: pt_BR,
                      })
                    : '-'}
                </td>
                <td>{coupon.usos} usos</td>
                <td>
                  {coupon.limite_geral > 0 ? (
                    coupon.limite_geral - coupon.usos
                  ) : (
                    <span
                      className="material-icons-outlined"
                      style={{
                        fontSize: 16,
                        height: 18,
                        verticalAlign: 'inherit',
                        marginRight: 3,
                      }}
                    >
                      all_inclusive
                    </span>
                  )}
                  <span> cupons</span>
                </td>
                <td>
                  <StatusField color={handleStatusColors(coupon.ativo)}>
                    <span>{coupon.ativo ? 'ATIVO' : 'INATIVO'}</span>
                  </StatusField>
                </td>
                {hasPermission() && (
                  <td>
                    <Actions>
                      <button
                        type="button"
                        onClick={() =>
                          history.push(`/cupom-desconto/editar/${coupon.id}`)
                        }
                        disabled={!hasPermission()}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        className="danger"
                        onClick={() => handleDeleteDiscountCoupon(coupon.id)}
                        disabled={!hasPermission()}
                      >
                        Excluir
                      </button>
                    </Actions>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
        <Pagination>
          <p>
            Mostrando <strong>{coupons.length}</strong> registros . Total:{' '}
            <strong>{coupons.length}</strong> registros
          </p>
          <div>
            <p>
              Limite por página: <strong>100 registros</strong>
            </p>
            <button
              type="button"
              disabled
              onClick={() => {
                // do nothing.
              }}
            >
              <MdChevronLeft />
            </button>
            <span>1</span>
            <button
              type="button"
              disabled
              onClick={() => {
                // do nothing.
              }}
            >
              <MdChevronRight />
            </button>
          </div>
        </Pagination>
      </Wrapper>
    </Container>
  );
};

export default DiscountCoupon;
